/*
 * math-banner: Canvas 2D の淡い数学文様(波形/三角網/リサージュ/点描)
 * three.js 不使用の軽量アクセント。reduced-motion 時は静止1フレームのみ描画。
 */
(() => {
  if (customElements.get('math-banner')) return;

  const gauss = () => (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;

  class MathBanner extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('.math-banner__canvas');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      this.pattern = this.dataset.pattern || 'wave';
      this.alpha = parseFloat(this.dataset.opacity) || 0.06;
      this.animate =
        this.hasAttribute('data-animate') &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.phase = 0;
      this.pts = null;

      this.onResize = () => {
        const dpr = Math.min(window.devicePixelRatio, 2);
        this.w = this.canvas.width = this.canvas.clientWidth * dpr;
        this.h = this.canvas.height = this.canvas.clientHeight * dpr;
        this.dpr = dpr;
        this.pts = null; // 三角網の点は作り直す
        if (!this.animate) this.draw();
      };
      window.addEventListener('resize', this.onResize);
      this.onResize();

      // 画面内のときだけ描画ループを回す
      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          this.visible = e.isIntersecting;
          this.toggleLoop();
        }
      });
      this.io.observe(this);
      this.draw();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.rafId != null) cancelAnimationFrame(this.rafId);
      window.removeEventListener('resize', this.onResize);
      this.rafId = null;
    }

    toggleLoop() {
      if (!this.animate) return;
      if (this.visible && this.rafId == null) {
        const loop = () => {
          this.rafId = null;
          this.phase += 0.009;
          this.draw();
          if (this.visible) this.rafId = requestAnimationFrame(loop);
        };
        this.rafId = requestAnimationFrame(loop);
      } else if (!this.visible && this.rafId != null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    ink() {
      const v = getComputedStyle(this).getPropertyValue('--color-foreground') || '26,26,26';
      return `rgba(${v.trim()},${this.alpha})`;
    }

    draw() {
      const { ctx, w, h, dpr } = this;
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = this.ink();
      ctx.fillStyle = this.ink();
      ctx.lineWidth = dpr * 0.7;

      if (this.pattern === 'wave') {
        for (let n = 0; n < 3; n++) {
          ctx.beginPath();
          for (let i = 0; i <= 120; i++) {
            const x = (i / 120) * w;
            const y =
              h * (0.72 + n * 0.08) +
              Math.sin((i / 120) * Math.PI * 4 + this.phase + n * 1.3) * h * 0.05;
            i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
          }
          ctx.stroke();
        }
      } else if (this.pattern === 'triangles') {
        if (!this.pts) {
          this.pts = [];
          const n = Math.round(w / dpr / 40);
          for (let i = 0; i < n; i++) {
            this.pts.push({
              x: Math.random() * w,
              y: h * (0.5 + Math.random() * 0.5),
              vx: (Math.random() - 0.5) * 0.12 * dpr,
              vy: (Math.random() - 0.5) * 0.08 * dpr,
            });
          }
        }
        const thr = 110 * dpr;
        for (const p of this.pts) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > w) p.vx *= -1;
          if (p.y < h * 0.45 || p.y > h) p.vy *= -1;
        }
        for (let i = 0; i < this.pts.length; i++) {
          for (let k = i + 1; k < this.pts.length; k++) {
            const dx = this.pts[i].x - this.pts[k].x;
            const dy = this.pts[i].y - this.pts[k].y;
            if (dx * dx + dy * dy < thr * thr) {
              ctx.beginPath();
              ctx.moveTo(this.pts[i].x, this.pts[i].y);
              ctx.lineTo(this.pts[k].x, this.pts[k].y);
              ctx.stroke();
            }
          }
        }
      } else if (this.pattern === 'lissajous') {
        const cx = w / 2, cy = h / 2, A = Math.min(w, h) * 0.36;
        ctx.beginPath();
        for (let i = 0; i <= 900; i++) {
          const t = (i / 900) * Math.PI * 2;
          const x = cx + A * 1.6 * Math.sin(3 * t + this.phase);
          const y = cy + A * Math.sin(4 * t);
          i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.stroke();
      } else if (this.pattern === 'stipple') {
        // 点描: 決定的な擬似乱数で描く(フレーム間でチラつかないように)
        let seed = 12345;
        const rand = () => {
          seed = (seed * 1103515245 + 12345) % 2147483648;
          return seed / 2147483648;
        };
        const n = Math.round((w * h) / (dpr * dpr) / 900);
        for (let i = 0; i < n; i++) {
          const x = rand() * w;
          const yBase = rand();
          const y = h * (0.35 + yBase * 0.65) + Math.sin(x / 60 + this.phase) * h * 0.02;
          const s = (0.6 + rand()) * dpr;
          ctx.fillRect(x, y, s, s);
        }
      }
    }
  }

  customElements.define('math-banner', MathBanner);
})();
